import { forwardRef, useImperativeHandle } from 'react';

import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import { z } from 'zod';

import { Form, FormField } from '@/components';

import { useAccountData } from './AccountDataContext';

// Updated schema to align with API WorkExperienceRequest structure
const workExperienceItemSchema = z.object({
  position: z
    .string()
    .min(
      1,
      t(
        'components:adminCandidates.addCandidate.workExperience.positionRequired'
      )
    ),
  company: z.string().optional(),
  industry: z.string().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional().nullable(),
  isStillWorking: z.boolean().optional().default(false),
});

const workExperienceSchema = z.object({
  experiences: z.array(workExperienceItemSchema),
});

export type WorkExperienceFormFields = z.infer<typeof workExperienceSchema>;
export type WorkExperienceItemFormFields = z.infer<
  typeof workExperienceItemSchema
>;

export interface WorkExperienceFormRef {
  submit: () => void;
}

export const WorkExperienceForm = forwardRef<WorkExperienceFormRef>(
  (_props, ref) => {
    const { t } = useTranslation(['common', 'candidate', 'cv', 'components']);
    const { data, updateWorkExperience } = useAccountData();

    // Create proper default work experience item
    const createDefaultWorkExperience = (): WorkExperienceItemFormFields => ({
      position: '',
      company: '',
      industry: '',
      startDate: undefined,
      endDate: undefined,
      isStillWorking: false,
    });

    // Transform API data to form format
    const getFormDefaultValues = (): WorkExperienceFormFields => {
      if (!data?.workExperience?.length) {
        return {
          experiences: [createDefaultWorkExperience()],
        };
      }

      const transformedExperiences = data.workExperience.map((exp) => ({
        position: exp.position ?? '',
        isStillWorking: exp.isStillWorking ?? false,
        company: exp.company ?? '',
        industry: exp.industry ?? '',
        startDate: exp.startDate ? new Date(exp.startDate) : undefined,
        endDate: exp.endDate ? new Date(exp.endDate) : undefined,
      }));

      return { experiences: transformedExperiences };
    };

    const form = useForm<WorkExperienceFormFields>({
      mode: 'onBlur',
      resolver: zodResolver(workExperienceSchema),
      values: getFormDefaultValues(),
    });

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: 'experiences',
    });

    // Expose submit function to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        form.handleSubmit(handleSubmit)();
      },
    }));

    const handleSubmit = async (formData: WorkExperienceFormFields) => {
      // Transform form data to API format - convert dates to strings
      const transformedExperiences = formData.experiences.map((experience) => ({
        ...experience,
        startDate: experience.startDate?.toISOString(),
        endDate: experience.endDate?.toISOString(),
      }));

      await updateWorkExperience(transformedExperiences);
    };

    return (
      <Form {...form} onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {fields.map((field, idx) => (
            <Box
              key={field.id}
              borderBottom={idx === fields.length - 1 ? 'none' : '1px solid'}
              borderColor="gray.50"
              pb={4}
            >
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontWeight="semibold" fontSize={16}>
                  {t(
                    'components:adminCandidates.addCandidate.workExperience.title'
                  )}{' '}
                  {fields.length > 1 ? idx + 1 : ''}
                </Text>
                <Button
                  onClick={() => append(createDefaultWorkExperience())}
                  colorScheme="brand"
                  variant="link"
                  leftIcon={
                    <Icon as={LuPlus} fontSize={20} color="brand.500" />
                  }
                >
                  {t('common:actions.addNew')}
                </Button>
              </Flex>

              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                gap={4}
                mt={4}
              >
                <FormField
                  control={form.control}
                  name={`experiences.${idx}.position`}
                  label={t('common:information.position')}
                  placeholder={t('common:information.position')}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`experiences.${idx}.industry`}
                  label={t(
                    'components:adminCandidates.addCandidate.workExperience.sector'
                  )}
                  placeholder={t(
                    'components:adminCandidates.addCandidate.workExperience.sector'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`experiences.${idx}.company`}
                  label={t(
                    'components:adminCandidates.addCandidate.workExperience.companyName'
                  )}
                  placeholder={t(
                    'components:adminCandidates.addCandidate.workExperience.companyName'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <HStack gap={2}>
                  <FormField
                    control={form.control}
                    name={`experiences.${idx}.startDate`}
                    label={t('common:timeAndDate.startDate')}
                    type="date"
                    size={{ base: 'sm', md: 'md' }}
                  />
                  <FormField
                    control={form.control}
                    name={`experiences.${idx}.endDate`}
                    label={t('common:timeAndDate.endDate')}
                    type="date"
                    size={{ base: 'sm', md: 'md' }}
                    isDisabled={form.watch(`experiences.${idx}.isStillWorking`)}
                  />
                </HStack>
              </Grid>
              <Flex justify="space-between" mt={4} gap={2}>
                {fields.length > 1 ? (
                  <Button
                    variant="link"
                    color="red.500"
                    onClick={() => remove(idx)}
                    leftIcon={
                      <Icon as={LuTrash2} fontSize={20} color="red.500" />
                    }
                  >
                    {t('common:actions.delete')}
                  </Button>
                ) : (
                  <Box />
                )}
                <FormField
                  type="custom"
                  control={form.control}
                  name={`experiences.${idx}.isStillWorking`}
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          colorScheme="brand"
                          size={{ base: 'sm', md: 'md' }}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'components:adminCandidates.addCandidate.workExperience.stillWorking'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
              </Flex>
            </Box>
          ))}
        </VStack>
      </Form>
    );
  }
);

WorkExperienceForm.displayName = 'WorkExperienceForm';
